import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';

import {
	Column,
	ContextMenuItem,
	DataStateChangeEventArgs,
	GridComponent,
} from '@syncfusion/ej2-angular-grids';
import { Observable, Subscription } from 'rxjs';
import { ColumnMenuItem } from '@syncfusion/ej2-grids/src/grid/base/enum';
import { ClickEventArgs } from '@syncfusion/ej2-angular-buttons';
import { MsCrudService } from '../../services/ms-crud/ms-crud.services';
import { MsServiceCoreService } from '../../services/ms-service-core/ms-service-core.services';
import { ParseDataToValue } from '../../../core/utils/parse-data-value.util.service';
import { AccountService } from '../../../core/auth/account.service';
import { Authority } from '../../../config/authority.constants';

interface Privelege {
	new: boolean;
	view: boolean;
	edit: boolean;
	delete: boolean;
}

interface MsTableAction {
	width?: number;
	create?: any;
	read?: any;
	update?: any;
	delete?: any;
}

interface MsTableSort {
	field: string;
	direction: 'ascending' | 'descending';
}

interface MsTableColumn {
	type: 'number' | 'string' | 'boolean' | 'date' | 'datetime';
	field: string;
	header: string;
	onAction?: (...args: any) => void;
}

export interface MsTable {
	entityName: string;
	columns: MsTableColumn[];
	sorts?: MsTableSort[];
	actions: MsTableAction;
	enableVirtualization?: boolean;
	privelege?: Privelege;
}

@Component({
	selector: 'ms-table',
	templateUrl: './ms-table.component.html',
	styleUrls: ['./ms-table.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class MsTableComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input({ required: true }) service: MsCrudService<any>;
	@Input({ required: true }) table: MsTable;

	@ViewChild('grid')
	grid: GridComponent;

	data: Observable<DataStateChangeEventArgs>;
	loadingIndicator: object = { indicatorType: 'Shimmer' };
	filterSettings: object = {
		type: 'Menu',
		enableInfiniteScrolling: false,
		showFilterBarOperator: true,
		showFilterBarStatus: false,
	};
	toolbar: any[] = [
		'Search',
		{
			text: '',
			tooltipText: 'Limpar os filtros',
			prefixIcon: 'e-filter-clear',
			id: 'ClearFilter',
			align: 'Left',
		},
		{
			text: '',
			tooltipText: 'Atualizar',
			prefixIcon: 'e-refresh',
			id: 'Refresh',
			align: 'Left',
		},
		{
			text: '',
			tooltipText: 'Exibir/Ocultar Coluna(s)',
			prefixIcon: 'e-columns',
			id: 'ColumnChooser',
			align: 'Left',
		},
		{
			text: '',
			tooltipText: 'Agrupar',
			prefixIcon: 'e-group-2',
			id: 'Grouping',
			align: 'Left',
		},
		{ text: '', tooltipText: 'Imprimir', prefixIcon: 'e-print', id: 'Print', align: 'Right' },
		{
			text: '',
			tooltipText: 'Exportar em Excel',
			prefixIcon: 'e-excelexport',
			id: 'ExcelExport',
			align: 'Right',
		},
		{
			text: '',
			tooltipText: 'Exportar em Csv',
			prefixIcon: 'e-csvexport',
			id: 'CsvExport',
			align: 'Right',
		},
		{
			text: '',
			tooltipText: 'Exportar em Pdf',
			prefixIcon: 'e-pdfexport',
			id: 'PdfExport',
			align: 'Right',
		},
	];
	columnChooserSettings: { operator: 'contains'; ignoreAccent: true };
	contextMenuItems?: ContextMenuItem[] = [
		'AutoFit',
		'AutoFitAll',
		'SortAscending',
		'SortDescending',
		'Copy',
		'PdfExport',
		'ExcelExport',
		'CsvExport',
		'FirstPage',
		'PrevPage',
		'LastPage',
		'NextPage',
		'Group',
		'Ungroup',
	];
	columnMenuItems?: ColumnMenuItem[] = [
		'AutoFitAll',
		'AutoFit',
		'Group',
		'Ungroup',
		'SortAscending',
		'SortDescending',
		'ColumnChooser',
		'Filter',
	];
	selectionSettings: object = {
		type: 'Multiple',
		persistSelection: false,
		checkboxOnly: false,
	};
	isAllowGrouping: boolean = false;
	pageOptions: object = {
		pageSizes: ['30', '50', '100', 'All'],
		pageSize: 30,
		pageCount: 20,
	};

	callbackActionRef!: Subscription;
	/**
	 * Constructor
	 */
	constructor(
		public parseDataToValue: ParseDataToValue,
		private _msServiceCoreService: MsServiceCoreService,
		private _accountService: AccountService
	) {}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.callbackActionRef = this._msServiceCoreService
			.callbackAction()
			.subscribe((systemEvent) => {
				if (systemEvent.serviceName === this.service.getServiceName()) {
					if (systemEvent.action === 'callback_delete') {
						this.onGridRefresh();
					}
				}
			});
		this.data = this.service.getDataGrid();
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.callbackActionRef.unsubscribe();
	}

	/**
	 * On After View Init
	 */
	ngAfterViewInit(): void {
		this._load();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	dataStateChange(state: DataStateChangeEventArgs): void {
		if (state && state.action.requestType !== 'stringfilterrequest') {
			// this.service.executeGrid(this.grid, state);
			this.onGridLoad(state);
		}
	}

	dataLoad(): void {
		(this.grid.columns[0] as Column).filterTemplate = '<span></span>';
	}

	dataBound() {
		this.grid.autoFitColumns(['id']);
	}

	onToolbarClick(args: ClickEventArgs) {
		if (args['item']['id'] === 'Add') {
			this.table.actions.create();
		}
		if (args['item']['id'] === 'ClearFilter') {
			this.grid.clearFiltering();
			this.grid.clearSorting();
			this.grid.search('');
		}
		if (args['item']['id'] === 'Refresh') {
			this.grid.refresh();
		}
		if (args['item']['id'] === 'Print') {
			this._onRemoveFirstColumnAndCall('print');
		}
		if (args['item']['id'] === 'ExcelExport') {
			this._onRemoveFirstColumnAndCall('excelexport');
		}
		if (args['item']['id'] === 'CsvExport') {
			this._onRemoveFirstColumnAndCall('csvexport');
		}
		if (args['item']['id'] === 'PdfExport') {
			this._onRemoveFirstColumnAndCall('pdfexport');
		}
		if (args['item']['id'] === 'ColumnChooser') {
			this.grid.columnChooserModule.openColumnChooser(8, 40);
		}
		if (args['item']['id'] === 'Grouping') {
			this.isAllowGrouping = !this.isAllowGrouping;
		}
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Private methods
	// -----------------------------------------------------------------------------------------------------
	private onGridLoad(state?: DataStateChangeEventArgs): void {
		let _sorted = { sorted: [{ name: 'id', direction: 'descending' }] };
		if (this.table.sorts) {
			_sorted = {
				sorted: this.table.sorts
					.map((item) => {
						return { name: item.field, direction: item.direction };
					})
					.reverse(),
			};
		}
		const _state: DataStateChangeEventArgs = {
			...state,
			..._sorted,
		};
		if (!state) {
			state = _state;
		} else if (!state.sorted) {
			state = _state;
		}
		this.service.executeGrid(this.grid, state);
	}

	private onGridRefresh(): void {
		//this.grid.refresh();
		this.onGridLoad();
	}

	private _onRemoveFirstColumnAndCall(_void: any): void {
		(this.grid.columns[0] as Column).visible = false;
		if (_void === 'print') {
			this.grid.print();
		} else if (_void === 'pdfexport') {
			this.grid.pdfExport();
		} else if (_void === 'csvexport') {
			this.grid.csvExport();
		} else if (_void === 'excelexport') {
			this.grid.excelExport();
		}
		setTimeout(() => {
			(this.grid.columns[0] as Column).visible = true;
		}, 1000);
	}

	private _privilegeHasAuthority(): void {
		let privilegeNew = this._accountService.hasAnyAuthority([this.table.entityName + '_CREATE']);
		let privilegeView = this._accountService.hasAnyAuthority([this.table.entityName + '_VIEW']);
		let privilegeEdit = this._accountService.hasAnyAuthority([this.table.entityName + '_UPDATE']);
		let privilegeDelete = this._accountService.hasAnyAuthority([this.table.entityName + '_DELETE']);
		let privilegeAdmin = this._accountService.hasAnyAuthority([Authority.ADMIN]);

		if (privilegeAdmin) {
			privilegeNew = true;
			privilegeView = true;
			privilegeEdit = true;
			privilegeDelete = true;
		}

		this.table.privelege = {
			new: privilegeNew,
			view: privilegeView,
			edit: privilegeEdit,
			delete: privilegeDelete,
		};
		let tableActionsWidth = 0;
		if (privilegeEdit) {
			tableActionsWidth += 50;
		}
		if (privilegeView) {
			tableActionsWidth += 50;
		}
		if (privilegeDelete) {
			tableActionsWidth += 50;
		}
		this.table.actions.width = tableActionsWidth;
	}

	private _showButtonNew(): void {
		if (this.table.actions.create && this.table.privelege?.new) {
			this.toolbar.unshift({
				text: '',
				tooltipText: 'Novo',
				prefixIcon: 'e-add',
				id: 'Add',
				align: 'Left',
			});
		}
	}

	private _load(): void {
		this.onGridLoad();
		this._privilegeHasAuthority();
		this._showButtonNew();
	}
}
